// One andon, as the BFF would send it, with every field filled in — so a test
// or the kitchen sink states only the part it cares about.
//
// Shared rather than copied into each test file, because the shape has
// forty-odd fields and five test files each carrying their own copy would be
// five places a new field has to be added before the type checks again. The
// kitchen sink reads the same fixtures to draw the thread with no BFF behind
// it, which is the other reason this is not a `.test.ts`.
//
// The values are the Andon app's own test fixtures: a two-star missing part
// at Steel Cutting, raised by Sione, a minute ago.

import type {
  AlertDetailVM,
  AlertEventVM,
  AndonAlertVM,
  CausesVM,
  StopForecastVM,
  ThreadMessageVM,
} from '@/api/andon/types';

export const FIXTURE_NOW = 1_700_000_000_000;

export function andonAlert(over: Partial<AndonAlertVM> = {}): AndonAlertVM {
  return {
    id: 1,
    qa: 'AN00001',
    stars: 2,
    type: 'materials',
    typeLabel: 'Missing part',
    routes: 'Stores',
    title: 'A part is short',
    stage: 'new',
    stageLabel: 'NEW',
    workcentre: 'Steel Cutting',
    flowline: 'Flowline 1',
    production: 'MO/41240',
    project: null,
    raisedBy: 'Sione Tupou',
    raisedAt: FIXTURE_NOW - 60_000,
    elapsedMs: 60_000,
    owner: { name: null, role: null, unowned: true, uid: null },
    sla: { msLeft: 120_000, fraction: 0.5, breaching: false, totalMs: 300_000, dueAt: FIXTURE_NOW + 120_000, phase: 'acknowledge' },
    acked: false,
    acknowledgedByName: null,
    acknowledgedByRole: null,
    etaMinutes: null,
    responding: false,
    respondingAt: null,
    fixEta: null,
    tier: 1,
    slaBreached: false,
    repeatCount: 1,
    clusterLabel: null,
    downtimeMinutes: 0,
    downtimeBasis: 'none',
    decision: null,
    taskOpenCount: 0,
    taskOwedNames: null,
    closedBy: null,
    closedAt: null,
    qualityAlertId: null,
    resolvedByMe: false,
    resolvedMs: null,
    ackMs: null,
    open: true,
    mine: false,
    raisedByMe: false,
    forMe: false,
    stopForecast: null,
    needsStopEstimate: true,
    ...over,
  };
}

/** The same andon with somebody on it: Ben took it, five minutes promised. */
export function ownedAlert(over: Partial<AndonAlertVM> = {}): AndonAlertVM {
  return andonAlert({
    stage: 'responding',
    stageLabel: 'RESPONDING',
    owner: { name: 'Ben Curtis', role: 'Stores', unowned: false, uid: 7 },
    sla: { msLeft: null, fraction: 1, breaching: false, totalMs: 300_000, dueAt: null, phase: 'responding' },
    acked: true,
    acknowledgedByName: 'Ben Curtis',
    acknowledgedByRole: 'Stores',
    etaMinutes: 5,
    responding: true,
    respondingAt: FIXTURE_NOW - 30_000,
    ...over,
  });
}

/** A whole StopForecastVM, so a test only states the part it cares about. */
export function stopForecast(over: Partial<StopForecastVM> = {}): StopForecastVM {
  return {
    predictedAt: FIXTURE_NOW + 60_000,
    basis: 'estimated',
    msLeft: 60_000,
    due: false,
    lapsed: false,
    confirmDueAt: FIXTURE_NOW + 60_000,
    ...over,
  };
}

export function andonDetail(over: Partial<AlertDetailVM> = {}): AlertDetailVM {
  return {
    ...andonAlert(),
    questions: {
      asked: [
        { question: 'Which pod or project is this for?', answer: 'MO/41240 · Type AA' },
        { question: 'Is the whole trolley missing, or one part?', answer: 'One part' },
        { question: 'Which part is missing?', answer: 'Door jamb · DJ-120' },
      ],
      question: null,
    },
    closeout: {
      stoppedAt: null,
      restartedAt: null,
      stopProposal: { stoppedAt: FIXTURE_NOW - 60_000, restartedAt: FIXTURE_NOW },
      minutes: 0,
      basis: 'none',
      calendar: 'Steel Cutting',
      category: null,
      subcategory: null,
      causedAt: null,
      causeConfirmed: false,
      corrective: null,
      pods: [{ id: 41240, name: 'MO/41240' }],
    },
    ladder: [
      { who: 'Stores', note: 'Notified the moment you raised it', state: 'hot' },
      { who: 'Dean Whitcombe', note: 'Auto-escalates at 2:00', state: 'waiting' },
      { who: 'Production Manager', note: 'Auto-escalates at 7:00', state: 'waiting' },
    ],
    notified: [{ label: 'Stores', role: 'Tier 1', tier: 1, uid: null }],
    words: 'Door jamb short on the C2 rack, none in the offcut bin either.',
    attachments: [],
    followers: 3,
    tasks: [],
    ...over,
  };
}

export function threadMessages(): ThreadMessageVM[] {
  return [
    {
      kind: 'raise',
      id: 101,
      body: 'Door jamb short on the C2 rack, none in the offcut bin either.',
      author: 'Sione Tupou',
      authorUid: null,
      authorEmployeeId: 12,
      at: '2023-11-14 22:12:00',
      mine: false,
      attachments: [],
      reactions: [],
    },
    {
      kind: 'normal',
      id: 102,
      body: 'On my way — have you tried the rack behind the saw?',
      author: 'Ben Curtis',
      authorUid: 7,
      authorEmployeeId: null,
      at: '2023-11-14 22:13:10',
      mine: false,
      attachments: [],
      reactions: [{ emoji: '👍', count: 1, mine: true, who: ['Sione Tupou'] }],
    },
    {
      kind: 'normal',
      id: 103,
      body: 'Two minutes',
      author: 'Ben Curtis',
      authorUid: 7,
      authorEmployeeId: null,
      at: '2023-11-14 22:13:40',
      mine: false,
      attachments: [],
      reactions: [],
    },
  ];
}

export function alertEvents(): AlertEventVM[] {
  return [
    { id: 201, type: 'notified', lead: 'Stores told', detail: 'the moment it was raised', at: '2023-11-14 22:12:01' },
    { id: 202, type: 'took', lead: 'Ben Curtis took it', detail: 'here in 5 min', at: '2023-11-14 22:13:00' },
  ];
}

/** Three classes, one of them without reasons — the shape the close sheet's gate turns on. */
export function causes(): CausesVM {
  return {
    classes: [
      {
        id: 1,
        key: 'materials',
        label: 'Materials',
        reasons: [
          { id: 11, key: 'ordered_late', label: 'Ordered late' },
          { id: 12, key: 'not_sure', label: 'Not sure' },
        ],
      },
      { id: 2, key: 'machine', label: 'Machine', reasons: [{ id: 21, key: 'breakdown', label: 'Breakdown' }] },
      { id: 3, key: 'unknown', label: 'Unknown / insufficient info', reasons: [] },
    ],
    byType: { materials: ['materials', 'unknown'] },
  };
}
